/**
 * Hub Customers — everyone attributed to this CP through their code.
 * Contact details arrive masked from the backend and are shown as-is; there
 * is no CP route to unmask them.
 */
import * as React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ArrowRight, Search, Users } from 'lucide-react';
import { formatDate } from '@finmin/utils';
import {
  Button,
  ErrorMessage,
  Input,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@finmin/ui';
import { useChannelPartnerCustomers, type ChannelPartnerCustomer } from '@/api/channel-partners';
import { initials } from '../format';
import { useDebouncedValue } from '../use-debounced-value';
import { HubEmptyState, HubPager } from './hub-bits';

const PAGE_SIZE = 20;

export function ChannelPartnerHubCustomers() {
  const navigate = useNavigate();
  const [search, setSearch] = React.useState('');
  const [page, setPage] = React.useState(1);
  const name = useDebouncedValue(search.trim());

  // A new search always starts from the first page.
  React.useEffect(() => setPage(1), [name]);

  const list = useChannelPartnerCustomers({ page, page_size: PAGE_SIZE, name: name || undefined });
  const items = list.data?.items ?? [];

  function openServices(c: ChannelPartnerCustomer) {
    void navigate({
      to: '/channel-partner/hub/services',
      search: { customer: c.id, customerName: c.name ?? undefined },
    });
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-semibold tracking-tight text-foreground">
            Customers Onboarded
          </h2>
          <p className="text-sm text-muted-foreground">
            {list.data ? `${list.data.total} customer${list.data.total === 1 ? '' : 's'}` : ' '}
            {name && list.data ? ` matching “${name}”` : ''}
          </p>
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by customer name"
            aria-label="Search customers by name"
            className="pl-9"
          />
        </div>
      </div>

      <ErrorMessage message={list.error?.message ?? null} />

      {list.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <HubEmptyState
          icon={Users}
          title={name ? 'No customers match this name' : 'No customers yet'}
          description={
            name
              ? 'Try a different spelling or clear the search.'
              : 'Share your Channel Partner code. Customers who sign up with it appear here.'
          }
        />
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-hidden rounded-lg border border-border bg-card md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Mobile</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Onboarded</TableHead>
                  <TableHead className="text-right">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((c) => (
                  <TableRow key={c.id} className="cursor-pointer" onClick={() => openServices(c)}>
                    <TableCell>
                      <span className="flex items-center gap-3">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-bold text-foreground">
                          {initials(c.name)}
                        </span>
                        <span className="font-medium text-foreground">{c.name || 'Customer'}</span>
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{c.mobile ?? '—'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{c.email ?? '—'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{formatDate(c.onboarded_at)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          openServices(c);
                        }}
                      >
                        Services <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile cards */}
          <ul className="space-y-2 md:hidden">
            {items.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => openServices(c)}
                  className="flex w-full items-center gap-3 rounded-lg border border-border bg-card p-3 text-left transition-colors hover:bg-muted"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-bold text-foreground">
                    {initials(c.name)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-foreground">{c.name || 'Customer'}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {[c.mobile, c.email].filter(Boolean).join(' · ') || '—'}
                    </span>
                    <span className="block text-[11px] text-muted-foreground">
                      Onboarded {formatDate(c.onboarded_at)}
                    </span>
                  </span>
                  <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                </button>
              </li>
            ))}
          </ul>

          <HubPager
            page={page}
            pageSize={PAGE_SIZE}
            total={list.data?.total ?? 0}
            isFetching={list.isFetching}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}
